import React, {useState, useEffect} from 'react';
import {Box, Button, Typography, Stack, useMediaQuery, useTheme} from '@mui/material';
import {useAdminAccess} from 'packages/common/hooks/useAdminAccess';
import {useRouter} from 'packages/common/routes/hooks';
import {ADMIN_BANNER} from 'packages/common/layouts/config-layout';
import Iconify from 'packages/common/components/iconify';

/**
 * Bannière d'admin access mode - Design épuré et professionnel
 *
 * Affiche une bannière FIXÉE en haut de l'écran quand un admin accède
 * au compte d'un client en mode support. Hauteur : 40px.
 *
 * @return {JSX.Element|null} The rendered banner component or null if not in admin access mode.
 */
export const AdminAccessBanner = () => {
  const {isAdminAccessMode, targetCompany, remainingTime, revokeAccess, reason, isTransitioning} = useAdminAccess();
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [timeLeft, setTimeLeft] = useState(remainingTime);

  // Gestion du timer local
  useEffect(() => {
    if (isAdminAccessMode && remainingTime) {
      setTimeLeft(remainingTime);
      const interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isAdminAccessMode, remainingTime]);

  // Déconnexion automatique quand le timer atteint 0
  useEffect(() => {
    if (isAdminAccessMode && timeLeft === 0) {
      const handleExpiration = async () => {
        const result = await revokeAccess();
        if (result.success) {
          router.push('/admin');
          window.location.reload();
        }
      };
      handleExpiration();
    }
  }, [timeLeft, isAdminAccessMode, revokeAccess, router]);

  const handleExitAdminMode = async () => {
    const result = await revokeAccess();
    if (result.success) {
      router.push('/admin');
      window.location.reload();
    }
  };

  // Masquer la barre pendant la transition pour éviter le flash visuel
  if (!isAdminAccessMode || isTransitioning) return null;

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = remainingTime > 0 ? (timeLeft / remainingTime) * 100 : 0;
  const isLowTime = progress < 30;

  return (
    <Box
      sx={{
        'position': 'fixed',
        'top': 0,
        'left': 0,
        'right': 0,
        'height': `calc(${ADMIN_BANNER.HEIGHT}px + env(safe-area-inset-top, 0px))`,
        'paddingTop': 'env(safe-area-inset-top, 0px)',
        'background': 'linear-gradient(90deg, #d32f2f 0%, #f44336 50%, #d32f2f 100%)',
        'backgroundSize': '200% 100%',
        'animation': 'gradientShift 3s ease infinite',
        'boxShadow': '0 2px 8px rgba(211, 47, 47, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
        'zIndex': (theme) => theme.zIndex.appBar + 2,
        'overflow': 'hidden',
        '@keyframes gradientShift': {
          '0%, 100%': {backgroundPosition: '0% 50%'},
          '50%': {backgroundPosition: '100% 50%'},
        },
      }}
    >
      {/* Pattern diagonal subtil */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,.05) 10px, rgba(0,0,0,.05) 20px)',
          opacity: 0.3,
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: '100%',
          px: isMobile ? 1 : 3,
          position: 'relative',
          zIndex: 1,
          gap: isMobile ? 0.5 : 2,
        }}
      >
        {/* Partie gauche - Info admin */}
        <Stack direction="row" alignItems="center" spacing={isMobile ? 0.5 : 2} sx={{minWidth: 0, flex: 1}}>
          {/* Badge MODE ADMIN - toujours visible */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={isMobile ? 0.5 : 1}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.25)',
              px: isMobile ? 0.75 : 1.5,
              py: 0.5,
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              flexShrink: 0,
            }}
          >
            <Iconify icon="solar:shield-warning-bold-duotone" width={isMobile ? 16 : 18} sx={{color: '#fff'}} />
            {!isMobile && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#fff',
                  letterSpacing: '1px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                MODE ADMIN
              </Typography>
            )}
          </Stack>

          {/* Séparateur - masqué sur mobile */}
          {!isMobile && (
            <Box
              sx={{
                width: 2,
                height: 20,
                backgroundColor: 'rgba(255,255,255,0.3)',
              }}
            />
          )}

          {/* Nom de l'entreprise - tronqué sur mobile */}
          <Stack direction="row" alignItems="center" spacing={isMobile ? 0.5 : 1} sx={{minWidth: 0}}>
            <Iconify icon="solar:buildings-2-bold" width={isMobile ? 16 : 18} sx={{color: '#fff', opacity: 0.9, flexShrink: 0}} />
            <Typography
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                fontWeight: 600,
                color: '#fff',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {targetCompany?.name || targetCompany}
            </Typography>
          </Stack>

          {/* Raison - masquée sur mobile */}
          {reason && !isMobile && (
            <>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.5)',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.8)',
                  fontStyle: 'italic',
                }}
              >
                {reason}
              </Typography>
            </>
          )}
        </Stack>

        {/* Partie droite - Timer + Bouton */}
        <Stack direction="row" alignItems="center" spacing={isMobile ? 0.75 : 2} sx={{flexShrink: 0}}>
          {/* Timer */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={isMobile ? 0.5 : 1}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.25)',
              px: isMobile ? 0.75 : 1.5,
              py: 0.5,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: isLowTime ? '#ffeb3b' : 'rgba(255,255,255,0.2)',
              boxShadow: isLowTime ? '0 0 8px rgba(255,235,59,0.5)' : '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            <Iconify
              icon="solar:clock-circle-bold"
              width={isMobile ? 16 : 18}
              sx={{
                color: isLowTime ? '#ffeb3b' : '#fff',
              }}
            />
            <Typography
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                fontWeight: 700,
                fontFamily: 'monospace',
                color: isLowTime ? '#ffeb3b' : '#fff',
                minWidth: isMobile ? '35px' : '45px',
                textAlign: 'center',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Stack>

          {/* Bouton Quitter */}
          <Button
            size="small"
            onClick={handleExitAdminMode}
            startIcon={!isMobile && <Iconify icon="solar:logout-bold" width={16} />}
            sx={{
              'backgroundColor': 'rgba(0,0,0,0.3)',
              'color': '#fff',
              'fontSize': isMobile ? '0.7rem' : '0.75rem',
              'textTransform': 'none',
              'fontWeight': 700,
              'px': isMobile ? 1 : 2,
              'py': 0.5,
              'minWidth': isMobile ? 'auto' : 'inherit',
              'borderRadius': '6px',
              'border': '1px solid rgba(255,255,255,0.2)',
              'boxShadow': '0 2px 4px rgba(0,0,0,0.2)',
              'textShadow': '0 1px 2px rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.4)',
                borderColor: 'rgba(255,255,255,0.3)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              },
            }}
          >
            {isMobile ? (
              <Iconify icon="solar:logout-bold" width={16} />
            ) : (
              'Quitter'
            )}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
